import React, { useState, useRef } from 'react';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

const LigandTable = ({ ligandData }) => {

  const tableRef = useRef(null);

  const ligandColumns = [
    { field: 'anPosition', header: 'Position' },
    { field: 'ligand', header: 'Ligand' },
    { field: 'inchikey', header: 'InChIKey' },
    { field: 'annotation', header: 'Annotation' },
    { field: 'ccd', header: 'CCD' },
    { field: 'pdbId', header: 'PDB ID' },
    { field: 'distance', header: 'Distance (Å)' },
    { field: 'details', header: 'Atoms' }
  ];

   const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        anPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        pdbId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        inchikey: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        annotation: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        ligand: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        details: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
    });

  /** Global search */
  const value = filters['global'] ? filters['global'].value : '';

  const onGlobalFilterChange = (event) => {
        const value = event.target.value.replace(/\s+/g, "");
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
  };

   const clearFilter = () => {
     setFilters({
       global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        anPosition: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        annotation: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        pdbId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        inchikey: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        ligand: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        distance: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
        details: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
     })   };

  const renderNumericFilter = (options) => (
    <InputText
      type="number"
      value={options.value || ''}
      onChange={(e) => options.filterCallback(e.target.value, options.index)}
      placeholder="Enter number"
      className="p-column-filter"
    />
  );

  const renderLigandColumns = ligandColumns.map((col) => (
    <Column
      key={col.field}
      field={col.field}
      header={col.header}
      sortable
      filter
      filterDisplay="menu"
      dataType={col.isNumeric ? 'numeric' : 'text'}
      filterElement={col.isNumeric ? renderNumericFilter : undefined}
      filterPlaceholder={`Search by ${col.header}`}
    />
  ));

  const urlBodyTemplate = (rowData) => (
    <Button
      label=""
      icon="pi pi-external-link"
      className="p-button-rounded"
      onClick={() => rowData.url !== "#" && window.open(rowData.url, '_blank')}
      disabled={rowData.url === "#"}
    />
  );


    const tagsBodyTemplate = (rowData) => {
    if (!rowData.summary || !rowData.anPosition) return null;
    const annotations = rowData.summary;
    if (!annotations) return null;

    return (
      <div className="flex gap-1 justify-content-center">
        {annotations.is_functional && (
         <>
           <Tooltip target=".is_functional" />
           <i className="is_functional pi pi-cog p-1" style={{ color: '#708090' }} data-pr-tooltip="Functional Significance"
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {annotations.has_clinvar && (
         <>
          <Tooltip target=".has_clinvar" />
          <i className="has_clinvar pi pi-shield p-1" style={{ color: '#708090' }} data-pr-tooltip="Has ClinVar variant"
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {annotations.has_intra_contact && (
         <>
          <Tooltip target=".has_intra_contact" />
          <i className="has_intra_contact pi pi-globe p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Intra-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
        {annotations.has_af_intra_contact && (
         <>
          <Tooltip target=".has_af_intra_contact" />
          <i className="has_af_intra_contact pi pi pi-star p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Intra-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
          {annotations.has_inter_contact && (
          <>
          <Tooltip target=".has_inter_contact" />
          <i className="has_inter_contact pi pi-sitemap p-1" style={{ color: '#708090' }} data-pr-tooltip="Has Inter-protein Contact"
           data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
          </>
        )}
        {annotations.has_af_inter_contact && (
         <>
          <Tooltip target=".has_af_inter_contact" />
          <i className="has_af_inter_contact pi pi-star-fill p-1" style={{ color: '#708090' }} data-pr-tooltip="Has AlphaFold Inter-protein Contact"  data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"></i>
         </>
        )}
      </div>
    );
  };


  const exportCSV = () => tableRef.current.exportCSV({ selectionOnly: false });

  const exportExcel = async (dataToExport) => {
    const xlsx = await import('xlsx');
    const worksheet = xlsx.utils.json_to_sheet(dataToExport);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAsExcelFile(excelBuffer, 'data');
  };

  const saveAsExcelFile = (buffer, fileName) => {
    import('file-saver').then((module) => {
      if (module && module.default) {
        const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const EXCEL_EXTENSION = '.xlsx';
        const data = new Blob([buffer], { type: EXCEL_TYPE });
        module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
      }
    });
  };

  const exportPdf = (dataToExport, columns) => {
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF.default('landscape');
        const formattedColumns = columns.map(col => ({ title: col.header, dataKey: col.field }));
        doc.autoTable(formattedColumns, dataToExport);
        doc.save('data.pdf');
      });
    });
  };

   /** Header */
  const header = (
    <div className="row align-items-center">
      <div className="col mb-2 d-flex align-items-center">
        <IconField iconPosition="left" className="me-1">
          <InputIcon className="pi pi-search" />
          <InputText
            type="search"
            value={value || ''} onChange={onGlobalFilterChange}
            placeholder="Global Search"
          />
        </IconField>
        <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} />
      </div>
      <div className="col d-flex justify-content-end align-items-center">
        <Button icon="pi pi-file" className="p-button-rounded p-mr-2" onClick={exportCSV} tooltip="CSV" />
        <Button
          icon="pi pi-file-excel"
          className="p-button-success p-button-rounded p-mr-2"
          onClick={exportExcel}
          tooltip="XLSX"
        />
        <Button
          icon="pi pi-file-pdf"
          className="p-button-warning p-button-rounded"
          onClick={exportPdf}
          tooltip="PDF"
        />
      </div>
    </div>
  );

  return (
    <DataTable
      ref={tableRef}
      value={ligandData}
      paginator
      rows={5}
      removableSort
      header={header}
      filters={filters}
      onFilter={(e) => setFilters(e.filters)}
      globalFilterFields={ligandColumns.map((c) => c.field)}
      sortOrder={1}
      className="nobreak"
      tableStyle={{ minWidth: '60rem' }}
    >
      <Column
          header="Tags"
          body={tagsBodyTemplate}
          style={{ width: '5rem' }}
          align="center"
      />
      {renderLigandColumns}
      <Column field="url" header="ChEMBL" body={urlBodyTemplate} />
    </DataTable>
  );
};

export default LigandTable;
